import {defineRecipe} from '@chakra-ui/react3'
import {SECTION_BG_VAR} from '../../modules/get-header-bg-from-first-module'

const moduleContainerRecipe = defineRecipe({
    base: {
        // mark this as inline size container to allows children
        // to use container queries and dimension
        // useful to span full visible width without causing horizontal overflow
        containerType: 'inline-size',
        paddingX: 'var(--container-inline-padding)',
        // expose block spacing as vars so modules can bleed a background layer into it
        paddingTop: 'var(--container-block-padding-start, 0px)',
        paddingBottom: 'var(--container-block-padding-end, 0px)',
        '--container-inline-padding': {
            base: 'spacing.container-padding-mobile',
            s: 'spacing.container-padding-desktop'
        },
        // use child selector to compute variables on the child container which applies the maxWidth
        '& > *': {
            // compute the applied padding and extra margin from the applied max-width from layerStyle
            // note: this could be simplified to `100cqw-50%`, but that depends on the property where it is used
            // and for scroll-padding it doesn't compute correctly as the percentage is from the scroll container
            '--container-total-inline-padding':
                'calc(var(--container-inline-padding, 0px) + max(0px, 50cqw - var(--max-container-width, 100cqw) * 0.5))'
        },
        // hide the container when no content is rendered
        '&:has(> :empty)': {
            display: 'none'
        }
    },
    variants: {
        modulePadding: {
            default: {
                '--container-inline-padding': {
                    base: 'spacing.container-padding-mobile',
                    s: 'spacing.container-padding-desktop'
                }
            },
            none: {
                '--container-inline-padding': 'spacing.spacing-0'
            },
            awards: {
                // mobile: default padding, desktop: fullwidth
                '--container-inline-padding': {s: 'spacing.spacing-0'}
            },
            highlightStack: {
                '--container-inline-padding': 'spacing.spacing-0'
            },
            hero: {
                // module fullwidth
                '--container-inline-padding': 'spacing.spacing-0'
            },
            media: {
                // module fullwidth
                '--container-inline-padding': 'spacing.spacing-0'
            },
            productColors: {
                // mobile, desktop: fullwidth
                '--container-inline-padding': 'spacing.spacing-0'
            },
            collage: {
                '--container-inline-padding': 'spacing.spacing-0'
            }
        },
        background: {
            default: {
                '--module-surface-bg-color': 'colors.background.surface.on-section-default',
                '--module-bg-color': 'colors.background.section.default',
                backgroundColor: SECTION_BG_VAR.default
            },
            subtle: {
                '--module-surface-bg-color': 'colors.background.surface.on-section-subtle',
                '--module-bg-color': 'colors.background.section.subtle',
                backgroundColor: SECTION_BG_VAR.subtle
            },
            strong: {
                '--module-surface-bg-color': 'colors.background.surface.on-section-strong',
                '--module-bg-color': 'colors.background.section.strong',
                backgroundColor: SECTION_BG_VAR.strong
            },
            inverse: {
                '--module-surface-bg-color': 'colors.background.surface.on-section-inverted',
                '--module-bg-color': 'colors.background.section.strong',
                backgroundColor: SECTION_BG_VAR.inverse
            },
            none: {
                // keep the container and module surface backgrounds transparent
                // modules can override --module-surface-bg-color or --module-bg-color locally when they need transparent module chrome with styled inner surfaces
                '--module-surface-bg-color': 'transparent',
                '--module-bg-color': 'transparent',
                backgroundColor: 'transparent'
            }
        },
        spacing: {
            default: {
                '--container-block-padding-start': {
                    base: 'spacing.spacing-64',
                    s: 'spacing.spacing-128'
                },
                '--container-block-padding-end': {
                    base: 'spacing.spacing-40',
                    s: 'spacing.spacing-128'
                }
            },
            reduced: {
                '--container-block-padding-start': 'spacing.spacing-40',
                '--container-block-padding-end': 'spacing.spacing-40'
            },
            min: {
                '--container-block-padding-start': 'spacing.spacing-20',
                '--container-block-padding-end': 'spacing.spacing-20'
            },
            'min-top': {
                '--container-block-padding-start': 'spacing.spacing-20',
                '--container-block-padding-end': {
                    base: 'spacing.spacing-40',
                    s: 'spacing.spacing-128'
                }
            },
            'min-bottom': {
                '--container-block-padding-start': {
                    base: 'spacing.spacing-64',
                    s: 'spacing.spacing-128'
                },
                '--container-block-padding-end': 'spacing.spacing-20'
            },
            none: {
                '--container-block-padding-start': 'spacing.spacing-0',
                '--container-block-padding-end': 'spacing.spacing-0'
            },
            recommender: {
                '--container-block-padding-start': {
                    base: 'spacing.spacing-40',
                    s: 'spacing.spacing-64'
                },
                '--container-block-padding-end': {
                    base: 'spacing.spacing-40',
                    s: 'spacing.spacing-64'
                }
            }
        }
    },
    defaultVariants: {
        background: 'default',
        modulePadding: 'default',
        spacing: 'default'
    }
})

export default moduleContainerRecipe
